/**
 * Token Refresh Utility
 * Automatically refreshes JWT tokens before they expire
 */
import axios from 'axios';
import { isUserInactive } from './activityTracker';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Check token every 15 minutes (less than inactivity timeout)
const CHECK_INTERVAL_MS = 15 * 60 * 1000;

let refreshInterval = null;

/**
 * Initialize automatic token refresh
 * Call this after user logs in
 */
export const startTokenRefresh = () => {
  // Clear any existing interval
  stopTokenRefresh();

  // Start new interval
  refreshInterval = setInterval(async () => {
    await checkAndRefreshToken();
  }, CHECK_INTERVAL_MS);

  // DON'T check immediately after login - the token is fresh!
  // This was causing a race condition where refresh happened before the first API call
  // checkAndRefreshToken();
};

/**
 * Stop automatic token refresh
 * Call this when user logs out
 */
export const stopTokenRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

/**
 * Check if token needs refresh and refresh if necessary
 */
export const checkAndRefreshToken = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      stopTokenRefresh();
      return;
    }
    
    // Don't refresh if user is inactive - let them timeout
    if (isUserInactive()) {
      return;
    }
    
    const response = await axios.post(
      `${API}/refresh-token`,
      { skipActivityUpdate: true }, // Tell backend not to count this as user activity
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    
    if (response.data.refreshed) {
      // Update token in localStorage
      localStorage.setItem('token', response.data.token);
      // Update axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    }
  } catch (error) {
    // If refresh fails, token might be invalid - stop trying
    if (error.response?.status === 401) {
      stopTokenRefresh();
      // Optionally trigger logout
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      window.location.href = '/signin';
    }
  }
};

/**
 * Manually refresh token
 * Can be called before important operations
 */
export const refreshToken = async () => {
  return await checkAndRefreshToken();
};

